import React, {
  createContext,
  useEffect,
  useContext,
  useState,
  useMemo,
  useCallback,
} from 'react'

import { useAuth0 } from '@auth0/auth0-react'
import { UserInfo } from '@harvey-frontend/models/user-info'
import * as Sentry from '@sentry/react'
import { isNil, isEmpty } from 'lodash'

import Services from 'services'

import { fetchUserInfo } from '../../../services/endpoints'

export interface AccessTokenType {
  isAuthenticated: boolean
  isAuthDialogOpen: boolean
  isLoadingAccessToken: boolean
  accessToken: string | null
  userInfo: UserInfo | null
  handleExistingAuth: () => void
  setIsAuthDialogOpen: (isAuthDialogOpen: boolean) => void
  setIsLoadingAccessToken: (isLoadingAccessToken: boolean) => void
  updateAccessToken: (newAccessToken: string | null) => void
  getAccessToken: () => Promise<string | null>
  logoutHandler: () => void
}

const initialAccessTokenContext: AccessTokenType = {
  isAuthenticated: false,
  isAuthDialogOpen: false,
  isLoadingAccessToken: false,
  accessToken: null,
  userInfo: null,
  handleExistingAuth: () => {},
  setIsAuthDialogOpen: () => {},
  setIsLoadingAccessToken: () => {},
  updateAccessToken: () => {},
  getAccessToken: () => Promise.resolve(null),
  logoutHandler: () => {},
}

export const AccessTokenContext = createContext<AccessTokenType>(
  initialAccessTokenContext
)

export const useAccessTokenContext = () => {
  return useContext(AccessTokenContext)
}

// We have two forms of authentication: Auth0 via localStorage & accessToken via the AuthDialog
// In Office 365 we can't rely on the localStorage because the AuthDialog and Taskpane are rendered via iFrames
// Therefore we pass back the accessToken from the AuthDialog to the Taskpane and use that to create the backend client
// As a result, the Taskpane Auth0 client doesn't know the user actually authenticated

// In Word Desktop client, AuthDialog and Taskpane share the same localStorage
// Therefore we can use the Auth0 client to check if the user is authenticated
export const AccessTokenProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const {
    isAuthenticated: isAuth0Authenticated,
    getAccessTokenSilently,
    logout,
  } = useAuth0()

  const [isAuthDialogOpen, setIsAuthDialogOpen] = useState<boolean>(false)
  const [isLoadingAccessToken, setIsLoadingAccessToken] =
    useState<boolean>(false)
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null)

  const getAccessToken = useCallback(async () => {
    try {
      if (isAuth0Authenticated) {
        const auth0Token = await getAccessTokenSilently()
        return auth0Token
      } else if (accessToken) {
        return accessToken
      }
    } catch (e) {
      Sentry.captureException(e)
      if (accessToken) return accessToken
    }
    return null
  }, [isAuth0Authenticated, accessToken, getAccessTokenSilently])

  const isAuthenticated = useMemo(() => {
    return (isAuth0Authenticated || !!accessToken) && !isEmpty(userInfo)
  }, [isAuth0Authenticated, accessToken, userInfo])

  const updateAccessToken = useCallback(
    async (newAccessToken: string | null) => {
      Services.Backend.AttachGetToken(async () => newAccessToken)
      if (isNil(newAccessToken)) {
        setUserInfo(null)
      } else {
        try {
          const userInfo = await fetchUserInfo()
          setUserInfo(userInfo)
          setAccessToken(newAccessToken)
        } catch (e) {
          // If fetchUserInfo fails, the user is not authenticated & the rest of the app
          // won't work. This can indicate the auth token expired, and since Office Addins
          // are weird about auth, the easiest way to refresh is to log them out. Tokens last
          // for 24h by default, so this should be rare.
          console.error('Error fetching user info', e)
          Sentry.captureException(e)
          setAccessToken(null)
          setUserInfo(null)
          logout({
            localOnly: true,
            returnTo: window.location.href,
          })
        }
      }
    },
    [setUserInfo, logout]
  )

  // Refresh the access token every 10 minutes.
  useEffect(() => {
    const interval = setInterval(
      async () => {
        const newAccessToken = await getAccessToken()
        if (newAccessToken) {
          await updateAccessToken(newAccessToken)
        }
      },
      10 * 60 * 1000
    )
    return () => clearInterval(interval)
  }, [getAccessToken, updateAccessToken])

  // In Word Desktop client, AuthDialog and Taskpane share the same localStorage
  // Therefore we can use the Auth0 client to check if the user is authenticated
  // In Office 365 the user is expected to login every-time after closing the Taskpane
  const handleExistingAuth = useCallback(async () => {
    const auth0Token = await getAccessTokenSilently()
    await updateAccessToken(auth0Token)
  }, [getAccessTokenSilently, updateAccessToken])

  const logoutHandler = useCallback(async () => {
    await updateAccessToken(null)
    logout({
      localOnly: true,
      returnTo: window.location.href,
    })
  }, [logout, updateAccessToken])

  // this useEffect is necessary to handle the case where the user is already authenticated
  // ie the user's cookies are already in our local-storage
  // in this case we need to create the backend client
  useEffect(() => {
    if (isAuth0Authenticated) {
      void handleExistingAuth()
    }
  }, [isAuth0Authenticated, handleExistingAuth])

  const value = useMemo(() => {
    return {
      isAuthenticated,
      isAuthDialogOpen,
      isLoadingAccessToken,
      accessToken,
      userInfo,
      handleExistingAuth,
      setIsAuthDialogOpen,
      setIsLoadingAccessToken,
      updateAccessToken,
      getAccessToken,
      logoutHandler,
    }
  }, [
    isAuthenticated,
    accessToken,
    isAuthDialogOpen,
    isLoadingAccessToken,
    userInfo,
    handleExistingAuth,
    updateAccessToken,
    getAccessToken,
    logoutHandler,
  ])

  return (
    <AccessTokenContext.Provider value={value}>
      {children}
    </AccessTokenContext.Provider>
  )
}
